<script setup lang="ts">
import { router, useForm, usePage } from '@inertiajs/vue3'
import {
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  Eye,
  Loader2,
  Mail,
  Pause,
  Pencil,
  Play,
  Plus,
  Trash2,
  Webhook,
  X,
  Zap,
} from 'lucide-vue-next'
import { ref, watch } from 'vue'

import RelativeTime from '@/components/RelativeTime.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCopyToClipboard } from '@/composables/useCopyToClipboard'
import { useDialog } from '@/composables/useDialog'
import { highlightJson } from '@/lib/syntax-highlight'

interface SubscribableEvent {
  key: string
  label: string
}

interface WebhookDelivery {
  id: number
  status: string
  event: string | null
  response_status: number | null
  delivered_at: string | null
  created_at: string
  payload: Record<string, unknown> | string | null
}

interface WebhookSubscription {
  id: number
  url: string
  events: string
  active: boolean
  created_at: string
  last_delivery: WebhookDelivery | null
  recent_deliveries: WebhookDelivery[]
}

interface NotificationPreference {
  email_enabled: boolean
  email_events: string[]
}

const props = defineProps<{
  recipientEmail: string
  notificationPreference: NotificationPreference
  subscribableEvents: SubscribableEvent[]
  webhookSubscriptions: WebhookSubscription[]
  preferencePath: string
  webhookCreatePath: string
  webhookPath: (id: number) => string
  webhookTestPath: (id: number) => string
}>()

const page = usePage()
const { copy, isCopied } = useCopyToClipboard()

// --- Webhook secret reveal — shown once right after creation ---
const showSecretDialog = ref(false)
const newWebhookSecret = ref<string | null>(null)

watch(
  () => (page.flash as Record<string, unknown> | undefined)?.webhook_secret as string | undefined,
  (secret) => {
    if (secret) {
      newWebhookSecret.value = secret
      showSecretDialog.value = true
    }
  },
  { immediate: true },
)

// --- Email preferences form ---
const emailForm = useForm({
  email_enabled: props.notificationPreference.email_enabled,
  email_events: [...props.notificationPreference.email_events],
})

function toggleEvent(key: string) {
  const idx = emailForm.email_events.indexOf(key)
  if (idx === -1) emailForm.email_events.push(key)
  else emailForm.email_events.splice(idx, 1)
}

function saveEmailPrefs() {
  emailForm.patch(props.preferencePath)
}

// --- Webhook form ---
const showWebhookForm = ref(false)
const webhookForm = useForm({
  url: '',
  events: [] as string[],
  active: true,
})

function toggleWebhookEvent(key: string) {
  const idx = webhookForm.events.indexOf(key)
  if (idx === -1) webhookForm.events.push(key)
  else webhookForm.events.splice(idx, 1)
}

function cancelWebhookForm() {
  showWebhookForm.value = false
  webhookForm.reset()
}

function createWebhook() {
  webhookForm.post(props.webhookCreatePath, {
    onSuccess: () => {
      showWebhookForm.value = false
      webhookForm.reset()
    },
  })
}

const { confirm } = useDialog()

async function confirmDelete(id: number, url: string) {
  const ok = await confirm({
    title: 'Remove webhook endpoint?',
    description: `${url} will be permanently removed and will no longer receive event deliveries.`,
    confirmLabel: 'Remove',
  })
  if (!ok) return
  router.delete(props.webhookPath(id))
}

function testWebhook(id: number) {
  router.post(props.webhookTestPath(id))
}

function toggleWebhookActive(sub: WebhookSubscription) {
  router.patch(
    props.webhookPath(sub.id),
    { active: !sub.active },
    {
      preserveScroll: true,
      onSuccess: () => router.reload({ only: ['webhook_subscriptions'] }),
    },
  )
}

// --- Edit webhook form ---
const editingId = ref<number | null>(null)
const editForm = useForm({ url: '', events: [] as string[] })

const expandedDeliveries = ref<Set<number>>(new Set())
const expandedPayloads = ref<Set<number>>(new Set())

function toggleDeliveries(id: number) {
  const s = new Set(expandedDeliveries.value)
  if (s.has(id)) s.delete(id)
  else s.add(id)
  expandedDeliveries.value = s
}

function togglePayload(id: number) {
  const s = new Set(expandedPayloads.value)
  if (s.has(id)) s.delete(id)
  else s.add(id)
  expandedPayloads.value = s
}

function startEdit(sub: WebhookSubscription) {
  editingId.value = sub.id
  editForm.url = sub.url
  editForm.events = sub.events
    ? sub.events
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean)
    : []
}

function cancelEdit() {
  editingId.value = null
  editForm.reset()
}

function saveEdit(id: number) {
  editForm.patch(props.webhookPath(id), {
    onSuccess: () => cancelEdit(),
  })
}

function toggleEditEvent(key: string) {
  const idx = editForm.events.indexOf(key)
  if (idx === -1) editForm.events.push(key)
  else editForm.events.splice(idx, 1)
}

function eventLabels(eventsStr: string) {
  const keys = eventsStr
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  return keys.map((k) => props.subscribableEvents.find((e) => e.key === k)?.label ?? k).join(', ')
}
</script>

<template>
  <div class="space-y-8">
    <!-- Email Notification Preferences -->
    <Card>
      <CardHeader>
        <div class="flex items-center gap-2">
          <Mail class="text-muted-foreground size-4" />
          <CardTitle class="text-base">Email Notifications</CardTitle>
        </div>
        <CardDescription>
          Choose which events you'd like to receive email notifications for. Emails are sent to
          <strong>{{ recipientEmail }}</strong
          >.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form class="space-y-4" @submit.prevent="saveEmailPrefs">
          <div v-if="subscribableEvents.length === 0" class="text-muted-foreground text-sm">
            No events available for subscription yet.
          </div>

          <div v-else class="space-y-3">
            <div v-for="event in subscribableEvents" :key="event.key" class="flex items-center gap-3">
              <Checkbox
                :id="`email-event-${event.key}`"
                :modelValue="emailForm.email_events.includes(event.key)"
                :disabled="!emailForm.email_enabled"
                @update:modelValue="toggleEvent(event.key)"
              />
              <Label :for="`email-event-${event.key}`" class="cursor-pointer font-normal">
                {{ event.label }}
              </Label>
            </div>
          </div>

          <div class="flex justify-end pt-2">
            <Button type="submit" size="sm" :disabled="emailForm.processing">
              <Loader2 v-if="emailForm.processing" class="mr-2 size-3.5 animate-spin" />
              Save Preferences
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>

    <!-- Webhook Endpoints -->
    <Card>
      <CardHeader>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Webhook class="text-muted-foreground size-4" />
            <CardTitle class="text-base">Webhook Endpoints</CardTitle>
          </div>
          <Button size="sm" variant="outline" @click="showWebhookForm = !showWebhookForm">
            <Plus class="mr-1.5 size-3.5" />
            Add Endpoint
          </Button>
        </div>
        <CardDescription>
          Receive real-time HTTP POST requests to your server when events occur. Requests are signed with
          <code class="text-xs">X-TLC-Signature</code> (HMAC-SHA256).
        </CardDescription>
      </CardHeader>
      <CardContent class="space-y-4">
        <!-- Add webhook form -->
        <div v-if="showWebhookForm" class="space-y-4 rounded-md border p-4">
          <p class="text-sm font-medium">New Endpoint</p>
          <div class="grid gap-2">
            <Label for="webhook-url">HTTPS URL <span class="text-destructive">*</span></Label>
            <Input
              id="webhook-url"
              v-model="webhookForm.url"
              type="url"
              placeholder="https://yourapp.com/webhooks/tlc"
              required
            />
            <p v-if="webhookForm.errors.url" class="text-destructive text-xs">{{ webhookForm.errors.url }}</p>
          </div>
          <div class="space-y-2">
            <Label>Events</Label>
            <div v-for="event in subscribableEvents" :key="event.key" class="flex items-center gap-3">
              <Checkbox
                :id="`wh-event-${event.key}`"
                :modelValue="webhookForm.events.includes(event.key)"
                @update:modelValue="toggleWebhookEvent(event.key)"
              />
              <Label :for="`wh-event-${event.key}`" class="cursor-pointer font-normal">{{ event.label }}</Label>
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" @click="cancelWebhookForm"> Cancel </Button>
            <Button size="sm" :disabled="webhookForm.processing" @click="createWebhook">
              <Loader2 v-if="webhookForm.processing" class="mr-2 size-3.5 animate-spin" />
              Save Endpoint
            </Button>
          </div>
        </div>

        <!-- Existing webhooks -->
        <div
          v-if="webhookSubscriptions.length === 0 && !showWebhookForm"
          class="text-muted-foreground py-8 text-center text-sm"
        >
          No webhook endpoints configured yet.
        </div>

        <ul v-else class="space-y-3">
          <li
            v-for="sub in webhookSubscriptions"
            :key="sub.id"
            class="space-y-3 rounded-md border p-4"
            :class="{ 'opacity-60': !sub.active && editingId !== sub.id }"
          >
            <!-- Edit mode -->
            <template v-if="editingId === sub.id">
              <p class="text-sm font-medium">Edit Endpoint</p>
              <div class="grid gap-2">
                <Label :for="`edit-url-${sub.id}`">HTTPS URL <span class="text-destructive">*</span></Label>
                <Input
                  :id="`edit-url-${sub.id}`"
                  v-model="editForm.url"
                  type="url"
                  placeholder="https://yourapp.com/webhooks/tlc"
                  required
                />
                <p v-if="editForm.errors.url" class="text-destructive text-xs">{{ editForm.errors.url }}</p>
              </div>
              <div class="space-y-2">
                <Label>Events</Label>
                <div v-for="event in subscribableEvents" :key="event.key" class="flex items-center gap-3">
                  <Checkbox
                    :id="`edit-event-${sub.id}-${event.key}`"
                    :modelValue="editForm.events.includes(event.key)"
                    @update:modelValue="toggleEditEvent(event.key)"
                  />
                  <Label :for="`edit-event-${sub.id}-${event.key}`" class="cursor-pointer font-normal">{{
                    event.label
                  }}</Label>
                </div>
              </div>
              <div class="flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" @click="cancelEdit">
                  <X class="mr-1.5 size-3.5" />
                  Cancel
                </Button>
                <Button size="sm" :disabled="editForm.processing" @click="saveEdit(sub.id)">
                  <Loader2 v-if="editForm.processing" class="mr-2 size-3.5 animate-spin" />
                  Save
                </Button>
              </div>
            </template>

            <!-- Display mode -->
            <template v-else>
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <a
                      :href="sub.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center gap-1 truncate font-mono text-sm hover:underline"
                    >
                      {{ sub.url }}
                      <ExternalLink class="size-3 shrink-0" />
                    </a>
                    <Badge
                      :variant="sub.active ? 'default' : 'secondary'"
                      :class="sub.active ? 'bg-green-600 hover:bg-green-600' : ''"
                    >
                      {{ sub.active ? 'Active' : 'Paused' }}
                    </Badge>
                  </div>
                  <p class="text-muted-foreground mt-1 text-xs">
                    Events: {{ eventLabels(sub.events) || 'None selected' }}
                  </p>
                </div>
                <div class="flex shrink-0 items-center gap-1.5">
                  <Button size="sm" variant="ghost" title="Edit endpoint" @click="startEdit(sub)">
                    <Pencil class="size-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" title="Send test ping" @click="testWebhook(sub.id)">
                    <Zap class="size-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    :title="sub.active ? 'Pause' : 'Resume'"
                    @click="toggleWebhookActive(sub)"
                  >
                    <Pause v-if="sub.active" class="size-3.5" />
                    <Play v-else class="size-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    class="text-destructive hover:text-destructive"
                    @click="confirmDelete(sub.id, sub.url)"
                  >
                    <Trash2 class="size-3.5" />
                  </Button>
                </div>
              </div>

              <!-- Last delivery status + history toggle -->
              <div class="text-muted-foreground flex items-center gap-1.5 border-t pt-2 text-xs">
                <template v-if="sub.last_delivery">
                  <Badge
                    :variant="
                      sub.last_delivery.status === 'failed'
                        ? 'destructive'
                        : sub.last_delivery.status === 'delivered'
                          ? 'default'
                          : 'secondary'
                    "
                    :class="sub.last_delivery.status === 'delivered' ? 'bg-green-600 hover:bg-green-600' : ''"
                  >
                    {{ sub.last_delivery.status }}
                    <template v-if="sub.last_delivery.response_status">
                      ({{ sub.last_delivery.response_status }})
                    </template>
                  </Badge>
                  Last attempt: <RelativeTime :date="sub.last_delivery.created_at" />
                </template>
                <template v-else> No deliveries yet </template>
                <Button
                  v-if="sub.recent_deliveries.length > 0"
                  size="sm"
                  variant="ghost"
                  class="ml-auto h-5 px-1.5 text-xs"
                  @click="toggleDeliveries(sub.id)"
                >
                  <ChevronUp v-if="expandedDeliveries.has(sub.id)" class="mr-1 size-3" />
                  <ChevronDown v-else class="mr-1 size-3" />
                  {{ sub.recent_deliveries.length }} deliveries
                </Button>
              </div>

              <!-- Delivery history -->
              <div v-if="expandedDeliveries.has(sub.id) && sub.recent_deliveries.length > 0" class="border-t pt-2">
                <table class="w-full text-xs">
                  <thead>
                    <tr class="text-muted-foreground border-b">
                      <th class="pb-1.5 text-left font-medium">Event</th>
                      <th class="pb-1.5 text-left font-medium">Status</th>
                      <th class="pb-1.5 text-left font-medium">When</th>
                      <th class="pb-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="delivery in sub.recent_deliveries" :key="delivery.id">
                      <tr
                        class="border-b border-dashed"
                        :class="{ 'last:border-0': !expandedPayloads.has(delivery.id) }"
                      >
                        <td class="py-1.5 pr-3 font-mono">{{ delivery.event ?? '—' }}</td>
                        <td class="py-1.5 pr-3">
                          <Badge
                            :variant="
                              delivery.status === 'failed'
                                ? 'destructive'
                                : delivery.status === 'delivered'
                                  ? 'default'
                                  : 'secondary'
                            "
                            :class="
                              delivery.status === 'delivered'
                                ? 'bg-green-600 text-[10px] hover:bg-green-600'
                                : 'text-[10px]'
                            "
                          >
                            {{ delivery.status }}
                            <template v-if="delivery.response_status"> ({{ delivery.response_status }})</template>
                          </Badge>
                        </td>
                        <td class="text-muted-foreground py-1.5">
                          <RelativeTime :date="delivery.created_at" />
                        </td>
                        <td class="py-1.5 pl-2 text-right">
                          <Button
                            v-if="delivery.payload"
                            size="sm"
                            variant="ghost"
                            class="h-5 px-1.5 text-xs"
                            @click="togglePayload(delivery.id)"
                          >
                            <ChevronUp v-if="expandedPayloads.has(delivery.id)" class="size-3" />
                            <ChevronDown v-else class="size-3" />
                          </Button>
                        </td>
                      </tr>
                      <tr
                        v-if="expandedPayloads.has(delivery.id) && delivery.payload"
                        class="border-b border-dashed last:border-0"
                      >
                        <td colspan="4" class="pt-1 pb-2">
                          <pre
                            class="bg-muted overflow-x-auto rounded p-2 text-[10px] leading-relaxed break-all whitespace-pre-wrap"
                            v-html="highlightJson(delivery.payload)"
                          ></pre>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </template>
          </li>
        </ul>
      </CardContent>
    </Card>
  </div>

  <!-- Webhook secret reveal dialog — shown once on creation -->
  <Dialog v-model:open="showSecretDialog">
    <DialogContent class="sm:max-w-md [&>button:last-child]:hidden" @interact-outside.prevent @escape-key-down.prevent>
      <DialogHeader>
        <DialogTitle>Webhook Created</DialogTitle>
        <DialogDescription> Copy this secret now. It will <strong>not</strong> be shown again. </DialogDescription>
      </DialogHeader>
      <div class="space-y-3">
        <div class="bg-muted flex items-center gap-2 rounded-md px-3 py-2">
          <Eye class="text-muted-foreground size-4 shrink-0" />
          <code class="flex-1 text-xs break-all">{{ newWebhookSecret }}</code>
          <Button
            variant="ghost"
            size="icon"
            class="size-7 shrink-0"
            @click="copy(newWebhookSecret!, 'webhook-secret')"
          >
            <Copy class="size-3.5" />
          </Button>
        </div>
        <p v-if="isCopied('webhook-secret')" class="text-muted-foreground text-center text-xs">Copied!</p>
      </div>
      <DialogFooter>
        <Button @click="showSecretDialog = false">Done</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
